/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.1
 * @since 1.19.0
 */

import { Fragment, memo , useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { useSelector } from 'react-redux';

import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageSubscriberMobile,
} from '@modules/internationalization/intSelectors';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { Table, useTableListApi, useTableMetaApi, useTableResizeApi } from '@modules/table';
import withErrorBoundaryHoc from '@modules/table/components/withErrorBoundaryHoc';
import {
    tableContext,
    tableIsErrorSelector,
    tableIsFastBasketPage,
    tableIsLoadingSelector,
} from '@modules/table/selectors';

import { makeSelectAuthSearchSettingsMsisdnFilterMaxSize } from '../../PageAuth/selectors/authSelectors';
import {
    tableSubscriberListenerMiddleware,
    tableSubscriberListenerStart,
} from '../middleware/tableSubscriberListenerMiddleware';
import SubscriberMobileTableFooter from './SubscriberMobileTableFooter';
import { TableSubscriberColumns } from './TableSubscriberColumns';

const TableSubscriber = () => {

    const pageTranslation = useSelector(makeSelectTranslationPageSubscriberMobile());
    const commonTranslation = useSelector(makeSelectTranslationCommon());

        /* Table Statuses  */
    const isLoading = useSelector(tableIsLoadingSelector);
    const isError = useSelector(tableIsErrorSelector);

    /* Получение meta таблицы */
    const isMultiSearchSelector = useMemo(
        () => makeSelectGuardComponentEnabled(AccessComponentKeyEnum.TableMsisdnMultiSearch), []);
    const isMultiSearchEnabled = useSelector(isMultiSearchSelector);

    const msisdnFilterMaxSize = useSelector(makeSelectAuthSearchSettingsMsisdnFilterMaxSize);

    // для страниц быстрых действий
    const isFastBasketPage = useSelector(tableIsFastBasketPage);
    /* Контекст для основных операций*/
    const context = useSelector(tableContext);

    const [isOpen, setOpen] = useState(true);

    const disableCheckedAllMessage = isFastBasketPage ? pageTranslation.tableFooter.linkMessage.tooltip : '';

    const config = useMemo(() => TableSubscriberColumns(
        pageTranslation,
        commonTranslation,
        isMultiSearchEnabled,
        msisdnFilterMaxSize,
        isFastBasketPage,
        disableCheckedAllMessage,
    ), [
        pageTranslation,
        commonTranslation,
        isMultiSearchEnabled,
        msisdnFilterMaxSize,
        isFastBasketPage,
        disableCheckedAllMessage,
    ]);

    const { buttons, buttonsEnabled, columns } = useTableMetaApi(config);
    const { data, isCompact, infiniteLoader } = useTableListApi();

       /* Table Resize */
    const { resize } = useTableResizeApi();

    const renderFooter = useCallback((HorScroll: ReactNode) => (
        <Fragment>
            {HorScroll}
            {!!data.length && (
                <SubscriberMobileTableFooter
                    buttons={buttons}
                    buttonsEnabled={buttonsEnabled}
                    isOpen={isOpen}
                    setOpen={setOpen}
                />
            )}
        </Fragment>
    ), [isOpen, data.length, buttons, buttonsEnabled]);

    useEffect(() => {
        tableSubscriberListenerStart(commonTranslation, context);
        return () => {
            tableSubscriberListenerMiddleware.clearListeners();
        };
    }, [commonTranslation, context]);

    if (columns.length < 2) return null;

    return (
        <Table
            columns={columns}
            data={data}
            dataName="subscriber/mobile"
            dataNameRow="msisdn"
            footer={renderFooter}
            infiniteScroll={infiniteLoader}
            isCompact={isCompact}
            isError={isError}
            isLoading={isLoading}
            onResize={resize}
        />
    );
};

export default memo(withErrorBoundaryHoc(TableSubscriber));
